import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HeroUIProvider } from '@heroui/react';

import { TransitionProvider } from './components/TransitionProvider';
import Preloader from './components/Preloader';
import HomePage from './components/HomePage';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import WorksPage from './components/WorksPage';
import ProjectPage from './components/ProjectPage';
import AnimationGallery from './components/AnimationGallery';
import { useLenis } from './hooks/useLenis';

import './index.css';

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const scrollToTop = () => {
      const lenis = typeof window !== 'undefined' ? window.__lenis : null;
      if (lenis?.scrollTo) {
        lenis.scrollTo(0, { immediate: true, lock: true });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    // Small delay to ensure Lenis is ready
    const timeoutId = setTimeout(scrollToTop, 0);
    return () => clearTimeout(timeoutId);
  }, [pathname, search]);

  return null;
}

function LenisScroller() {
  useLenis();
  return null;
}

function RouteScopedLoader({ onComplete }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Only show preloader on first load (check sessionStorage)
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');
    
    if (!hasLoadedBefore) {
      setShown(true);
    } else {
      onComplete?.();
    }
  }, [onComplete]);

  if (!shown) return null;
  return <Preloader onComplete={onComplete} />;
}

function AppContent() {
  const location = useLocation();
  const [loaderComplete, setLoaderComplete] = useState(false);

  const handleLoaderComplete = () => {
    setLoaderComplete(true);
  };

  return (
    <HeroUIProvider>
      <TransitionProvider>
        <LenisScroller />
        <ScrollToTop />
        {!loaderComplete && (
          <RouteScopedLoader onComplete={handleLoaderComplete} />
        )}
        <div className="App">
          <AnimatePresence mode="wait">
            <Routes location={location} key={`${location.pathname}${location.search}`}>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/works" element={<WorksPage />} />
              <Route path="/project" element={<ProjectPage />} />
              <Route path="/animations" element={<AnimationGallery />} />
            </Routes>
          </AnimatePresence>
        </div>
      </TransitionProvider>
    </HeroUIProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
